import axios from 'axios';
import type { AxiosError } from 'axios';
import type {
    AxiosInstance,
    AxiosRequestConfig,
    InternalAxiosRequestConfig,
} from 'axios';

import i18n from '@/i18n';
import type { ApiError } from '@/types';

/**
 * The HTTP client for the whole SPA.
 *
 * Sanctum's stateful guard authenticates by session cookie (ADR-001), so there is
 * no token to attach and nothing sensitive stored in JavaScript. What the client
 * does have to handle: the CSRF cookie handshake, telling the server which
 * language to answer in, and turning transport failures into a single predictable
 * error shape the UI can render.
 */

export const api: AxiosInstance = axios.create({
    baseURL: '/api/v1',
    // Sends the session and XSRF cookies. Without it, every request is anonymous.
    withCredentials: true,
    withXSRFToken: true,
    headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
    // Long enough for a report generation or an AI call to complete; short enough
    // that a hung request surfaces as an error rather than a spinner forever.
    timeout: 30_000,
});

/**
 * Tell the server which language to answer in.
 *
 * Server-side messages — validation errors, authorisation failures, notification
 * text — are translated by Laravel, not by the client. Sending the current locale
 * on every request is what keeps an Arabic UI from showing an English validation
 * error (§1.2).
 */
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    config.headers.set('X-Locale', i18n.language ?? 'ar');

    return config;
});

/** Raised for any failed API call, with the server's envelope preserved. */
export class ApiRequestError extends Error {
    constructor(
        message: string,
        readonly status: number,
        readonly code: string,
        readonly errors: Record<string, string[]> = {},
        readonly context: Record<string, unknown> = {},
    ) {
        super(message);
        this.name = 'ApiRequestError';
    }

    /** 422 — the request was understood but the data was rejected. */
    get isValidation(): boolean {
        return this.status === 422;
    }

    get isUnauthenticated(): boolean {
        return this.status === 401;
    }

    get isForbidden(): boolean {
        return this.status === 403;
    }

    get isNotFound(): boolean {
        return this.status === 404;
    }

    /** Did the session end through inactivity rather than sign-out? */
    get isSessionTimeout(): boolean {
        return this.code === 'SESSION_IDLE_TIMEOUT';
    }

    /** Worth retrying — a transport blip or a server-side transient. */
    get isRetryable(): boolean {
        return this.status === 0 || this.status === 429 || this.status >= 500;
    }

    /** The first error for a field, for inline form display. */
    fieldError(field: string): string | undefined {
        return this.errors[field]?.[0];
    }
}

/**
 * Listeners notified when the session ends server-side.
 *
 * A callback registry rather than a direct store import, because this module is
 * imported by the store itself — importing back would be a cycle. The auth store
 * registers itself on creation.
 */
type SessionEndedListener = (reason: 'timeout' | 'unauthenticated') => void;

const sessionEndedListeners = new Set<SessionEndedListener>();

export function onSessionEnded(listener: SessionEndedListener): () => void {
    sessionEndedListeners.add(listener);

    return () => sessionEndedListeners.delete(listener);
}

api.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiError>) => {
        const status = error.response?.status ?? 0;
        const payload = error.response?.data;

        const { code = fallbackCode(status), ...context } =
            payload?.error ?? {};

        /*
         * A 401 anywhere means the session is gone — expired, revoked, or the user
         * was signed out from another device by an administrator. Notifying
         * listeners here means every screen reacts, not just the one that happened
         * to make the failing request.
         *
         * The session-probe endpoint is excluded: it is *expected* to 401 for an
         * anonymous visitor on the login screen, and firing "your session ended"
         * at someone who never had one is nonsense.
         */
        if (status === 401 && !error.config?.url?.includes('session')) {
            const reason =
                code === 'SESSION_IDLE_TIMEOUT' ? 'timeout' : 'unauthenticated';

            sessionEndedListeners.forEach((listener) => listener(reason));
        }

        throw new ApiRequestError(
            payload?.message ?? networkMessage(status),
            status,
            code as string,
            payload?.errors ?? {},
            context as Record<string, unknown>,
        );
    },
);

function fallbackCode(status: number): string {
    if (status === 0) {
        return 'NETWORK_ERROR';
    }

    return `HTTP_${status}`;
}

function networkMessage(status: number): string {
    return status === 0
        ? i18n.t('common:state.offline')
        : i18n.t('common:state.error_description');
}

/**
 * Prime the CSRF cookie.
 *
 * Sanctum issues an `XSRF-TOKEN` cookie that axios echoes back as a header on
 * unsafe requests. It must be fetched before the first POST of a session, which
 * in practice means before signing in.
 */
export async function ensureCsrfCookie(): Promise<void> {
    await axios.get('/sanctum/csrf-cookie', { withCredentials: true });
}

/* Thin typed helpers, so callers do not repeat `.then((r) => r.data)` everywhere. */

export async function get<T>(
    url: string,
    config?: AxiosRequestConfig,
): Promise<T> {
    const { data } = await api.get<T>(url, config);

    return data;
}

export async function post<T>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig,
): Promise<T> {
    const { data } = await api.post<T>(url, body, config);

    return data;
}

export async function put<T>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig,
): Promise<T> {
    const { data } = await api.put<T>(url, body, config);

    return data;
}

export async function patch<T>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig,
): Promise<T> {
    const { data } = await api.patch<T>(url, body, config);

    return data;
}

export async function del<T>(
    url: string,
    config?: AxiosRequestConfig,
): Promise<T> {
    const { data } = await api.delete<T>(url, config);

    return data;
}

/**
 * Upload a file with progress.
 *
 * Used by branding assets now, and by complaint attachments and bulk customer
 * imports later — all of which need a progress indicator because the files are
 * large enough for silence to read as failure.
 */
export async function upload<T>(
    url: string,
    file: File,
    onProgress?: (percent: number) => void,
    fieldName = 'file',
): Promise<T> {
    const form = new FormData();
    form.append(fieldName, file);

    const { data } = await api.post<T>(url, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
        onUploadProgress: (event) => {
            if (onProgress && event.total) {
                onProgress(Math.round((event.loaded * 100) / event.total));
            }
        },
    });

    return data;
}

/**
 * A human-readable message for anything thrown by a request.
 *
 * Screens catch errors from two clients — the session client above and the
 * device client used by the kiosk and hall display — and a device screen that
 * rendered `[object Object]` at a customer is not something anybody would notice
 * in review but everybody would notice in a branch.
 */
export function messageFor(error: unknown): string {
    if (error instanceof ApiRequestError) {
        return error.message;
    }

    if (axios.isAxiosError(error)) {
        const payload = error.response?.data as ApiError | undefined;

        return payload?.message ?? networkMessage(error.response?.status ?? 0);
    }

    return error instanceof Error ? error.message : networkMessage(0);
}
